import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRateByClass } from 'utils/fieldglass';

import { Grid, Typography, Box } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Job } from '@libs/graphql-types';
import Chip from '@libs/ui/components/chip';
import { formatDateUS } from '@libs/ui/components/job/utils';
import { RouterLink } from '@libs/ui/components/typography';

import { JobAccordion } from './JobAccordion';

const useStyles = makeStyles((theme: any) => ({
  label: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(0.5),
  },
  value: {
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  skillsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  skill: {
    backgroundColor: 'rgba(38, 207, 187, 1)',
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'none',
  },
  description: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    marginBottom: theme.spacing(1),
  },
  readMore: {
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  descriptionContainer: {
    marginBottom: theme.spacing(2),
  },
}));

interface JobHighlightsAccordionProps {
  jobData: Job;
  expanded?: boolean;
  onChange?: (expanded: boolean) => void;
}

export const JobHighlightsAccordion: React.FC<JobHighlightsAccordionProps> = ({
  jobData,
  expanded,
  onChange,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('job');
  const [showFullDescription, setShowFullDescription] = useState(false);

  const DESCRIPTION_THRESHOLD = 300;

  // Map data from jobData and jobData.striive_job according to the CSV
  const striive = (jobData as any).striive_job || {};
  const skills =
    jobData.skills && Array.isArray(jobData.skills)
      ? jobData.skills.map((s: any) => s.name)
      : Array.isArray(striive.skills)
        ? striive.skills
        : [];

  const getValue = (val: any) =>
    val !== undefined && val !== null && val !== '' ? val : '-';

  const mappedData = {
    jobId: getValue(striive.record_id || jobData.id),
    jobName: getValue(striive.title || jobData.name),
    openInVMSDate: getValue(formatDateUS(striive.external_created_at)),
    jobStartDate: getValue(formatDateUS(striive.start_date)),
    jobEndDate: getValue(formatDateUS(striive.end_date)),
    availableOpenings: getValue(striive.number_of_positions),
    hiringManager: getValue(striive.report_to_full_name),
    company: getValue(striive.client_name),
    jobStage: getValue(striive.order_classification_status),
    requisitionOpenings: getValue(striive.max_submissions_allowed),
    standardBillRate: getValue(
      Array.isArray(striive.rates) && striive.rates.length > 0
        ? striive.rates[0]
        : striive.billAmount || '-',
    ),
    sourcingRecruiter: getValue(striive.recruiter_full_name),
    workType: getValue(striive.job_type),
    jobDescription: getValue(jobData.description || striive.content),
    skills: skills.length > 0 ? skills : [],
  };

  // Helper to safely truncate HTML string to N characters (without breaking tags)
  function truncateHtml(html: string, maxLength: number) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    if (text.length <= maxLength) return html;
    const truncated = text.slice(0, maxLength) + '...';
    return truncated;
  }

  function isLongDescription(html: string, maxLength: number) {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength;
  }

  const STRate = getRateByClass(jobData, 'ST');

  const renderDetailItem = (
    label: string,
    value: string | number,
    fullWidth = false,
  ) => (
    <Grid item xs={12} sm={fullWidth ? 12 : 6} md={fullWidth ? 12 : 4}>
      <Typography className={classes.label}>{label}</Typography>
      <Typography className={classes.value}>{value}</Typography>
    </Grid>
  );

  const renderDescription = (title: string, description: string) => {
    const longDescription = isLongDescription(
      description,
      DESCRIPTION_THRESHOLD,
    );
    return (
      <Grid item xs={12} className={classes.descriptionContainer}>
        <Typography className={classes.label}>{title}</Typography>
        <Typography className={classes.description}>
          <span
            dangerouslySetInnerHTML={{
              __html: showFullDescription
                ? description
                : truncateHtml(description, DESCRIPTION_THRESHOLD),
            }}
          />{' '}
          {longDescription && (
            <RouterLink
              to="#"
              className={classes.readMore}
              color="info.main"
              onClick={(e) => {
                e.preventDefault();
                setShowFullDescription((v) => !v);
              }}
            >
              {showFullDescription ? t('v2.showLess') : t('v2.accordion.readFullDescription')}
            </RouterLink>
          )}
        </Typography>
      </Grid>
    );
  };

  return (
    <JobAccordion
      title={t('v2.accordion.jobHighlights')}
      defaultExpanded
      expanded={expanded}
      onChange={onChange}
    >
      <Grid container spacing={3}>
        {renderDetailItem(t('v2.accordion.jobName'), mappedData.jobName, true)}
        {renderDetailItem(t('v2.accordion.jobId'), mappedData.jobId)}
        {renderDetailItem(t('v2.accordion.openInVmsDate'), mappedData.openInVMSDate)}
        {renderDetailItem(t('v2.accordion.jobStage'), mappedData.jobStage)}
        {renderDetailItem(t('v2.accordion.jobStartDate'), mappedData.jobStartDate)}
        {renderDetailItem(t('v2.accordion.jobEndDate'), mappedData.jobEndDate)}
        {renderDetailItem(t('v2.accordion.requisitionOpenings'), mappedData.requisitionOpenings)}
        {renderDetailItem(t('v2.accordion.availableOpenings'), mappedData.availableOpenings)}
        {renderDetailItem(t('v2.accordion.sourcingRecruiter'), mappedData.sourcingRecruiter)}
        {renderDetailItem(t('v2.accordion.standardBillRate'), STRate.billAmount)}
        {renderDetailItem(t('v2.accordion.hiringManager'), mappedData.hiringManager)}
        {renderDetailItem(t('v2.accordion.workType'), mappedData.workType)}
        {renderDetailItem(t('v2.accordion.company'), mappedData.company)}

        {renderDescription(t('v2.accordion.jobDescription'), mappedData.jobDescription)}

        {mappedData?.skills?.length > 0 && (
          <Grid item xs={12}>
            <Typography className={classes.label}>{t('v2.skills')}</Typography>
            <Box className={classes.skillsContainer}>
              {mappedData.skills.map((skill: string) => (
                <Chip
                  key={skill}
                  label={skill}
                  className={classes.skill}
                  size="small"
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    </JobAccordion>
  );
};
